import React, { useCallback, useEffect, useState } from "react";
import { apiRequest, apiUrl } from "../helpers/api";
import { BusinessProfileSkeleton } from "../components/skeleton/PageSkeletons";
import { getLoggedInUser, getToken, setAuthData } from "../helpers/authHelper";
import { showError, ToastFeedback } from "../components/ToastNotifications";
import supportedContentTypes from "../config/contentTypes";
import "./BusinessProfile.css";
import { Can } from "../components/AccessContext";
import ProductDetailsEditor, { normalizeProducts, productsForSave, validateProducts } from "../components/businessProfile/ProductDetailsEditor";

const emptyProfile = {
  brand_name: "",
  brand_description: "",
  industry: "",
  location: "",
  website_url: "",
  target_audience: "",
  customer_pain_points: "",
  products_services: "",
  differentiators: "",
  business_phone: "",
  country: "",
  state_city: "",
  business_address: "",
  company_size: "",
  brand_tone: "",
  brand_language: "",
  brand_guidelines: "",
  audience_age_range: "",
  audience_locations: "",
  audience_interests: "",
  custom_goal: "",
  content_goals: [],
  colors: [],
  products: [],
  posting_frequency: 3,
  preferred_platforms: [],
  preferred_content_types: [],
  logo_enabled: true,
  logo_placement: "Bottom Right",
  logo_size: 18,
  logo_opacity: 0.85
};
const editableFields = Object.keys(emptyProfile);
const textFields = [
  "brand_name", "brand_description", "industry", "location", "website_url", "target_audience",
  "customer_pain_points", "products_services", "differentiators", "business_phone", "country",
  "state_city", "business_address", "company_size", "brand_tone", "brand_language",
  "brand_guidelines", "audience_age_range", "audience_locations", "audience_interests", "custom_goal"
];

const goals = [
  ["brand_awareness", "Build brand awareness"],
  ["engagement", "Grow engagement"],
  ["leads", "Generate leads"],
  ["sales", "Increase sales"],
  ["website_traffic", "Drive website traffic"],
  ["conversions", "Increase conversions"],
  ["education", "Educate customers"],
  ["community", "Build community"],
  ["other", "Other"]
];
const platforms = [["instagram", "Instagram"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"], ["x", "X"], ["youtube", "YouTube"]];
const contentTypes = supportedContentTypes;
const voices = ["Friendly", "Professional", "Playful", "Authoritative", "Warm", "Educational"];
const logoPlacements = ["Top Left", "Top Center", "Top Right", "Center", "Bottom Left", "Bottom Center", "Bottom Right"];
const maxLogoBytes = 12 * 1024 * 1024;

const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeProfile = (profile) => {
  const normalized = { ...emptyProfile, ...(profile || {}) };
  for (const field of textFields) {
    if (typeof normalized[field] !== "string") normalized[field] = "";
  }
  for (const field of ["content_goals", "colors", "products", "preferred_platforms", "preferred_content_types"]) {
    normalized[field] = parseArray(normalized[field]).filter((value) => field === "products"
      ? Boolean(value && typeof value === "object" && !Array.isArray(value))
      : typeof value === "string");
  }
  normalized.products = normalizeProducts(normalized.products);
  if (!Number.isInteger(Number(normalized.posting_frequency)) || Number(normalized.posting_frequency) < 1 || Number(normalized.posting_frequency) > 7) normalized.posting_frequency = emptyProfile.posting_frequency;
  normalized.posting_frequency = Number(normalized.posting_frequency);
  if (!Number.isInteger(Number(normalized.logo_size)) || Number(normalized.logo_size) < 5 || Number(normalized.logo_size) > 50) normalized.logo_size = emptyProfile.logo_size;
  normalized.logo_size = Number(normalized.logo_size);
  if (!Number.isFinite(Number(normalized.logo_opacity)) || Number(normalized.logo_opacity) < 0.1 || Number(normalized.logo_opacity) > 1) normalized.logo_opacity = emptyProfile.logo_opacity;
  normalized.logo_opacity = Number(normalized.logo_opacity);
  normalized.logo_enabled = typeof normalized.logo_enabled === "boolean" ? normalized.logo_enabled : emptyProfile.logo_enabled;
  if (!logoPlacements.includes(normalized.logo_placement)) normalized.logo_placement = emptyProfile.logo_placement;
  return normalized;
};

const validateProfile = (profile, savedProductCount = 0) => {
  const required = [
    ["brand_name", "Business or brand name"],
    ["brand_description", "Business description"],
    ["industry", "Industry"],
    ["target_audience", "Target audience"],
    ["customer_pain_points", "Customer needs and challenges"],
    ["products_services", "Products or services"],
    ["differentiators", "What makes the business distinct"],
    ["brand_tone", "Brand voice"]
  ];
  for (const [field, label] of required) {
    if (!String(profile[field] || "").trim()) return `${label} is required.`;
  }
  if (!voices.includes(profile.brand_tone)) return "Choose a valid brand voice.";
  if (profile.content_goals.length < 1 || profile.content_goals.length > 4) return "Choose between 1 and 4 content goals.";
  if (profile.website_url.trim()) {
    try {
      const website = new URL(profile.website_url.trim());
      if (!["http:", "https:"].includes(website.protocol)) throw new Error();
    } catch {
      return "Enter a complete website URL beginning with https://.";
    }
  }
  if (profile.colors.some((color) => !/^#[\da-f]{6}$/i.test(color))) return "Use valid six-digit hex colors.";
  if (!Number.isInteger(Number(profile.posting_frequency)) || profile.posting_frequency < 1 || profile.posting_frequency > 7) return "Posting frequency must be between 1 and 7 posts per week.";
  return validateProducts(profile.products, { previousCount: savedProductCount });
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Unable to read the selected logo."));
  reader.onerror = () => reject(new Error("Unable to read the selected logo."));
  reader.readAsDataURL(file);
});

const TextField = ({ label, field, value, onChange, maxLength, multiline = false, type = "text", placeholder = "" }) => {
  const id = `business-profile-${field}`;
  return <div className="business-profile-field">
    <label className="form-label" htmlFor={id}>{label}</label>
    {multiline
      ? <textarea id={id} className="form-control" rows="3" maxLength={maxLength} value={value || ""} placeholder={placeholder} onChange={(event) => onChange(field, event.target.value)} />
      : <input id={id} className="form-control" type={type} maxLength={maxLength} value={value || ""} placeholder={placeholder} onChange={(event) => onChange(field, event.target.value)} />}
    {maxLength && <span className="business-profile-counter">{(value || "").length}/{maxLength}</span>}
  </div>;
};

const ProfileValue = ({ label, value, multiline = false }) => <div className="business-profile-value">
  <span>{label}</span>
  <strong className={multiline ? "is-multiline" : ""}>{value || "Not provided"}</strong>
</div>;

const BusinessProfile = () => {
  const [profile, setProfile] = useState(emptyProfile);
  const [savedProfile, setSavedProfile] = useState(emptyProfile);
  const [businessEmail, setBusinessEmail] = useState("");
  const editing = true;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest("/api/brand-settings/profile");
      const current = normalizeProfile(result.profile);
      setProfile(current);
      setSavedProfile(current);
      setBusinessEmail(result.businessEmail || "");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(!removeLogo && profile.logo ? apiUrl(profile.logo) : "");
      return undefined;
    }
    let current = true;
    readFileAsDataUrl(logoFile).then((preview) => {
      if (current) setLogoPreview(preview);
    }).catch((previewError) => {
      if (current) showError(previewError.message);
    });
    return () => { current = false; };
  }, [logoFile, profile.logo, removeLogo]);

  const setValue = (field, value) => setProfile((current) => ({ ...current, [field]: value }));
  const toggleListValue = (field, value) => setProfile((current) => ({
    ...current,
    [field]: current[field].includes(value) ? current[field].filter((item) => item !== value) : [...current[field], value]
  }));

  const discardChanges = () => {
    setProfile(normalizeProfile(savedProfile));
    setLogoFile(null);
    setRemoveLogo(false);
    setError("");
    setSuccess("");
  };

  const resetChanges = () => {
    setProfile(normalizeProfile(savedProfile));
    setLogoFile(null);
    setRemoveLogo(false);
    setError("");
    setSuccess("");
  };

  const selectLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)) {
      setError("Choose a PNG, JPEG, WebP, or GIF logo.");
      setLogoFile(null);
      event.target.value = "";
      return;
    }
    if (file.size > maxLogoBytes) {
      setError("Logo must be 12 MB or smaller.");
      setLogoFile(null);
      event.target.value = "";
      return;
    }
    setError("");
    setSuccess("");
    setLogoFile(file);
    setRemoveLogo(false);
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    const validationError = validateProfile(profile, savedProfile.products.length);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (uploadingImages) {
      setError("Wait for product images to finish uploading before saving.");
      return;
    }
    setSaving(true);
    let profileWasSaved = false;
    try {
      const result = await apiRequest("/api/brand-settings/profile", {
        method: "PUT",
        body: JSON.stringify(Object.fromEntries(editableFields.map((field) => [field, field === "products" ? productsForSave(profile.products) : profile[field]])))
      });
      profileWasSaved = true;
      if (logoFile) {
        await apiRequest("/api/brand-settings/logo", {
          method: "POST",
          body: JSON.stringify({ dataUrl: await readFileAsDataUrl(logoFile) })
        });
      } else if (removeLogo && profile.logo) {
        await apiRequest("/api/brand-settings/logo", { method: "DELETE" });
      }
      const latest = await apiRequest("/api/brand-settings/profile");
      const updated = normalizeProfile(latest.profile || result.profile);
      setProfile(updated);
      setSavedProfile(updated);
      setBusinessEmail(latest.businessEmail || businessEmail);
      setLogoFile(null);
      setRemoveLogo(false);
      setAuthData(getToken(), { ...getLoggedInUser(), onboardingComplete: Boolean(latest.onboardingComplete) });
      setSuccess("Your business profile has been saved.");
    } catch (requestError) {
      if (profileWasSaved) {
        await loadProfile();
      }
      showError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className="business-profile-page"><BusinessProfileSkeleton /></section>;

  if (error && !profile.brand_name) return <section className="business-profile-page">
    <ToastFeedback type="error" message={error} />
    <button type="button" className="business-profile-button is-primary" onClick={loadProfile}>Try again</button>
  </section>;

  return <section className="business-profile-page">
    <div className="business-profile-header">
      <div>
        <p className="business-profile-eyebrow">BUSINESS</p>
        <h1>Business Profile</h1>
        <p>Manage the information that shapes your brand and generated content.</p>
      </div>
    </div>

    {success && <ToastFeedback type="success" message={success} />}
    {error && <div className="business-profile-validation-error" role="alert">{error}</div>}

    {!editing ? <div className="business-profile-display">
      <section className="business-profile-card business-profile-identity">
        {profile.logo
          ? <img className="business-profile-logo" src={apiUrl(profile.logo)} alt={`${profile.brand_name} logo`} />
          : <span className="business-profile-logo-placeholder"><i className="ti ti-building-store" aria-hidden="true" /></span>}
        <div><h2>{profile.brand_name || "Your business"}</h2><p>{profile.industry || "Industry not provided"}{profile.location ? ` · ${profile.location}` : ""}</p>{businessEmail && <small>{businessEmail}</small>}</div>
      </section>

      <section className="business-profile-card">
        <header><h2>Business information</h2></header>
        <div className="business-profile-values">
          <ProfileValue label="Description" value={profile.brand_description} multiline />
          <ProfileValue label="Website" value={profile.website_url} />
          <ProfileValue label="Phone" value={profile.business_phone} />
          <ProfileValue label="Location" value={[profile.state_city, profile.country].filter(Boolean).join(", ") || profile.location} />
          <ProfileValue label="Business address" value={profile.business_address} />
          <ProfileValue label="Company size" value={profile.company_size} />
        </div>
      </section>

      <section className="business-profile-card">
        <header><h2>Audience and offer</h2></header>
        <div className="business-profile-values">
          <ProfileValue label="Target audience" value={profile.target_audience} multiline />
          <ProfileValue label="Customer needs and challenges" value={profile.customer_pain_points} multiline />
          <ProfileValue label="Audience age range" value={profile.audience_age_range} />
          <ProfileValue label="Audience locations" value={profile.audience_locations} />
          <ProfileValue label="Audience interests" value={profile.audience_interests} multiline />
          <ProfileValue label="Products and services" value={profile.products_services} multiline />
          <ProfileValue label="What makes you distinct" value={profile.differentiators} multiline />
          {profile.products.map((product, index) => <ProfileValue key={`${product.name}-${index}`} label={product.name || `Product or service ${index + 1}`} value={[product.category, product.description, product.price ? `Price: ${product.price}` : "", product.imageUrl].filter(Boolean).join(" · ")} multiline />)}
        </div>
      </section>

      <section className="business-profile-card">
        <header><h2>Voice and content preferences</h2></header>
        <div className="business-profile-values">
          <ProfileValue label="Brand voice" value={profile.brand_tone} />
          <ProfileValue label="Brand language" value={profile.brand_language} />
          <ProfileValue label="Brand guidelines" value={profile.brand_guidelines} multiline />
          <ProfileValue label="Marketing goals" value={profile.content_goals.map((goal) => goals.find(([key]) => key === goal)?.[1] || goal).join(", ")} />
          <ProfileValue label="Custom goal" value={profile.custom_goal} />
          <ProfileValue label="Posting frequency" value={`${profile.posting_frequency} posts per week`} />
          <ProfileValue label="Preferred platforms" value={profile.preferred_platforms.map((platform) => platforms.find(([key]) => key === platform)?.[1] || platform).join(", ")} />
          <ProfileValue label="Preferred content types" value={profile.preferred_content_types.join(", ")} />
          <ProfileValue label="Brand colors" value={profile.colors.join(", ")} />
          <ProfileValue label="Logo on generated content" value={profile.logo_enabled ? "Enabled" : "Disabled"} />
          <ProfileValue label="Logo placement" value={profile.logo_placement} />
          <ProfileValue label="Logo size" value={`${profile.logo_size}%`} />
          <ProfileValue label="Logo opacity" value={`${Math.round(profile.logo_opacity * 100)}%`} />
        </div>
      </section>
    </div> : <form className="business-profile-form" onSubmit={saveProfile} noValidate>
      <section className="business-profile-card">
        <header><div><h2>Business information</h2><p>Tell customers and Socialyorix what your business does.</p></div></header>
        <div className="business-profile-grid">
          <TextField label="Business or brand name" field="brand_name" value={profile.brand_name} onChange={setValue} maxLength={120} />
          <TextField label="Industry" field="industry" value={profile.industry} onChange={setValue} maxLength={120} placeholder="e.g. Specialty food, fitness, accounting" />
          <TextField label="What does your business do?" field="brand_description" value={profile.brand_description} onChange={setValue} maxLength={2000} multiline />
          <TextField label="Website" field="website_url" value={profile.website_url} onChange={setValue} maxLength={255} type="url" placeholder="https://example.com" />
          <TextField label="Business phone" field="business_phone" value={profile.business_phone} onChange={setValue} maxLength={40} type="tel" />
          <TextField label="Location / service area" field="location" value={profile.location} onChange={setValue} maxLength={160} placeholder="City, region or service area" />
          <TextField label="City or state" field="state_city" value={profile.state_city} onChange={setValue} maxLength={160} />
          <TextField label="Country" field="country" value={profile.country} onChange={setValue} maxLength={100} />
          <TextField label="Business address" field="business_address" value={profile.business_address} onChange={setValue} maxLength={500} />
          <TextField label="Company size" field="company_size" value={profile.company_size} onChange={setValue} maxLength={40} placeholder="e.g. 1-10 employees" />
        </div>
      </section>

      <section className="business-profile-card">
        <header><div><h2>Audience and offer</h2><p>Describe who you serve and what makes your offer valuable.</p></div></header>
        <div className="business-profile-grid">
          <TextField label="Who is your ideal customer?" field="target_audience" value={profile.target_audience} onChange={setValue} maxLength={2000} multiline />
          <TextField label="Customer needs and challenges" field="customer_pain_points" value={profile.customer_pain_points} onChange={setValue} maxLength={2000} multiline />
          <TextField label="Audience age range" field="audience_age_range" value={profile.audience_age_range} onChange={setValue} maxLength={40} placeholder="e.g. 25-40" />
          <TextField label="Audience locations" field="audience_locations" value={profile.audience_locations} onChange={setValue} maxLength={500} />
          <TextField label="Audience interests" field="audience_interests" value={profile.audience_interests} onChange={setValue} maxLength={2000} multiline />
          <TextField label="Products or services" field="products_services" value={profile.products_services} onChange={setValue} maxLength={2500} multiline />
          <TextField label="What makes the business distinct?" field="differentiators" value={profile.differentiators} onChange={setValue} maxLength={2000} multiline />
        </div>
        <ProductDetailsEditor
          products={profile.products}
          onChange={(products) => setValue("products", products)}
          onUploadingChange={setUploadingImages}
          inputClassName="form-control"
        />
      </section>

      <section className="business-profile-card">
        <header><div><h2>Voice and marketing goals</h2><p>Set the voice and outcomes your content should support.</p></div></header>
        <div className="business-profile-grid">
          <div className="business-profile-field">
            <label className="form-label" htmlFor="business-profile-brand_tone">Brand voice</label>
            <select id="business-profile-brand_tone" className="form-select" value={profile.brand_tone} onChange={(event) => setValue("brand_tone", event.target.value)}><option value="">Choose a voice</option>{voices.map((voice) => <option value={voice} key={voice}>{voice}</option>)}</select>
          </div>
          <TextField label="Brand language" field="brand_language" value={profile.brand_language} onChange={setValue} maxLength={80} placeholder="e.g. English" />
          <TextField label="Brand guidelines" field="brand_guidelines" value={profile.brand_guidelines} onChange={setValue} maxLength={5000} multiline />
        </div>
        <fieldset className="business-profile-choice-group">
          <legend>Marketing goals <span>(choose 1 to 4)</span></legend>
          <div className="business-profile-choices">{goals.map(([key, label]) => <label className="business-profile-choice" key={key}>
            <input type="checkbox" checked={profile.content_goals.includes(key)} disabled={!profile.content_goals.includes(key) && profile.content_goals.length >= 4} onChange={() => toggleListValue("content_goals", key)} />
            <span>{label}</span>
          </label>)}</div>
        </fieldset>
        {profile.content_goals.includes("other") && <TextField label="Describe your custom marketing goal" field="custom_goal" value={profile.custom_goal} onChange={setValue} maxLength={255} />}
      </section>

      <section className="business-profile-card">
        <header><div><h2>Content preferences and brand assets</h2><p>Control your publishing cadence, content mix, colors, and logo.</p></div></header>
        <div className="business-profile-grid">
          <div className="business-profile-field">
            <label className="form-label" htmlFor="business-profile-posting_frequency">Posting frequency</label>
            <select id="business-profile-posting_frequency" className="form-select" value={profile.posting_frequency} onChange={(event) => setValue("posting_frequency", Number(event.target.value))}>{[1, 2, 3, 4, 5, 6, 7].map((count) => <option value={count} key={count}>{count} posts per week</option>)}</select>
          </div>
        </div>
        <fieldset className="business-profile-choice-group">
          <legend>Preferred platforms</legend>
          <div className="business-profile-choices">{platforms.map(([key, label]) => <label className="business-profile-choice" key={key}>
            <input type="checkbox" checked={profile.preferred_platforms.includes(key)} onChange={() => toggleListValue("preferred_platforms", key)} /><span>{label}</span>
          </label>)}</div>
        </fieldset>
        <fieldset className="business-profile-choice-group">
          <legend>Preferred content types</legend>
          <div className="business-profile-choices">{contentTypes.map((type) => <label className="business-profile-choice" key={type}>
            <input type="checkbox" checked={profile.preferred_content_types.includes(type)} onChange={() => toggleListValue("preferred_content_types", type)} /><span>{type}</span>
          </label>)}</div>
        </fieldset>
        <fieldset className="business-profile-choice-group">
          <legend>Brand colors <span>(up to 7)</span></legend>
          <div className="business-profile-colors">
            {profile.colors.map((color, index) => <label key={`color-${index}`} className="business-profile-color"><span>Color {index + 1}</span><input aria-label={`Brand color ${index + 1}`} type="color" value={/^#[\da-f]{6}$/i.test(color) ? color : "#087e75"} onChange={(event) => setValue("colors", profile.colors.map((item, colorIndex) => colorIndex === index ? event.target.value : item))} /><button type="button" aria-label={`Remove brand color ${index + 1}`} onClick={() => setValue("colors", profile.colors.filter((_, colorIndex) => colorIndex !== index))}>Remove</button></label>)}
            {profile.colors.length < 7 && <button type="button" className="business-profile-button is-secondary" onClick={() => setValue("colors", [...profile.colors, "#087e75"])}>Add color</button>}
          </div>
        </fieldset>
        <div className="business-profile-logo-settings">
          <div className="business-profile-logo-preview">{logoPreview ? <img src={logoPreview} alt="Business logo preview" /> : <i className="ti ti-photo" aria-hidden="true" />}</div>
          <div className="business-profile-logo-controls">
            <label className="form-label" htmlFor="business-profile-logo">Business logo</label>
            <input id="business-profile-logo" className="form-control" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={selectLogo} />
            <span className="business-profile-help">PNG, JPEG, WebP, or GIF. Maximum 12 MB.</span>
            {(profile.logo || logoFile) && <Can permission="user.business_profile.edit"><button type="button" className="business-profile-remove" onClick={() => { setLogoFile(null); setRemoveLogo(true); }}>Remove logo</button></Can>}
          </div>
        </div>
        <div className="business-profile-grid business-profile-logo-options">
          <label className="business-profile-check"><input type="checkbox" checked={profile.logo_enabled} onChange={(event) => setValue("logo_enabled", event.target.checked)} /> Enable logo on generated content</label>
          <div className="business-profile-field"><label className="form-label" htmlFor="business-profile-logo_placement">Logo placement</label><select id="business-profile-logo_placement" className="form-select" value={profile.logo_placement} onChange={(event) => setValue("logo_placement", event.target.value)}>{logoPlacements.map((placement) => <option key={placement}>{placement}</option>)}</select></div>
          <div className="business-profile-field"><label className="form-label" htmlFor="business-profile-logo_size">Logo size ({profile.logo_size}%)</label><input id="business-profile-logo_size" className="form-range" type="range" min="5" max="50" value={profile.logo_size} onChange={(event) => setValue("logo_size", Number(event.target.value))} /></div>
          <div className="business-profile-field"><label className="form-label" htmlFor="business-profile-logo_opacity">Logo opacity ({Math.round(profile.logo_opacity * 100)}%)</label><input id="business-profile-logo_opacity" className="form-range" type="range" min="0.1" max="1" step="0.05" value={profile.logo_opacity} onChange={(event) => setValue("logo_opacity", Number(event.target.value))} /></div>
        </div>
      </section>

      <div className="business-profile-form-actions">
        <Can permission="user.business_profile.edit"><button type="button" className="business-profile-button is-secondary" disabled={saving} onClick={resetChanges}>Reset</button></Can>
        <div><button type="button" className="business-profile-button is-quiet" disabled={saving} onClick={discardChanges}>Cancel</button>
          <Can permission="user.business_profile.edit"><button type="submit" className="business-profile-button is-primary" disabled={saving || uploadingImages}>{saving ? <><span className="spinner-border spinner-border-sm" /> Saving...</> : uploadingImages ? "Uploading images..." : "Save Changes"}</button></Can></div>
      </div>
    </form>}
  </section>;
};

export default BusinessProfile;
